// src/schemas/auth.ts
import { z as z2 } from "zod";

// src/constants/season.ts
var SEASON_PRESETS = {
  mediterranean: {
    label: "Mediterranean",
    start: { month: 4, day: 1 },
    end: { month: 10, day: 31 }
  },
  caribbean: {
    label: "Caribbean",
    start: { month: 11, day: 1 },
    end: { month: 4, day: 30 }
  },
  southeast_asia: {
    label: "Southeast Asia",
    start: { month: 11, day: 1 },
    end: { month: 3, day: 31 }
  },
  australia: {
    label: "Australia",
    start: { month: 10, day: 1 },
    end: { month: 4, day: 30 }
  },
  year_round: {
    label: "Year Round",
    start: { month: 1, day: 1 },
    end: { month: 12, day: 31 }
  }
};
var SEASON_REGIONS = Object.keys(SEASON_PRESETS);
function toDate(sd, year) {
  return new Date(year, sd.month - 1, sd.day);
}
function isWrapping(region) {
  const preset = SEASON_PRESETS[region];
  const startMonth = preset.start.month;
  const endMonth = preset.end.month;
  return startMonth > endMonth;
}
function isInSeason(region, date = /* @__PURE__ */ new Date()) {
  if (region === "year_round") return true;
  const preset = SEASON_PRESETS[region];
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if (isWrapping(region)) {
    if (month > preset.start.month || month === preset.start.month && day >= preset.start.day) {
      return true;
    }
    if (month < preset.end.month || month === preset.end.month && day <= preset.end.day) {
      return true;
    }
    return false;
  }
  const afterStart = month > preset.start.month || month === preset.start.month && day >= preset.start.day;
  const beforeEnd = month < preset.end.month || month === preset.end.month && day <= preset.end.day;
  return afterStart && beforeEnd;
}
function getSeasonDates(region, date = /* @__PURE__ */ new Date()) {
  const preset = SEASON_PRESETS[region];
  const year = date.getFullYear();
  if (isWrapping(region)) {
    const month = date.getMonth() + 1;
    if (month < preset.end.month || month === preset.end.month && date.getDate() <= preset.end.day) {
      return {
        start: toDate(preset.start, year - 1),
        end: toDate(preset.end, year)
      };
    }
    return {
      start: toDate(preset.start, year),
      end: toDate(preset.end, year + 1)
    };
  }
  return {
    start: toDate(preset.start, year),
    end: toDate(preset.end, year)
  };
}
function getSeasonProgress(region, date = /* @__PURE__ */ new Date()) {
  if (region === "year_round") {
    const start2 = new Date(date.getFullYear(), 0, 1);
    const end2 = new Date(date.getFullYear(), 11, 31);
    const total2 = end2.getTime() - start2.getTime();
    const elapsed2 = date.getTime() - start2.getTime();
    return Math.round(elapsed2 / total2 * 100);
  }
  if (!isInSeason(region, date)) return 0;
  const { start, end } = getSeasonDates(region, date);
  const total = end.getTime() - start.getTime();
  const elapsed = date.getTime() - start.getTime();
  return Math.min(100, Math.max(0, Math.round(elapsed / total * 100)));
}
function getWeeksRemaining(region, date = /* @__PURE__ */ new Date()) {
  if (!isInSeason(region, date)) return 0;
  const { end } = getSeasonDates(region, date);
  const msRemaining = end.getTime() - date.getTime();
  return Math.max(0, Math.ceil(msRemaining / (7 * 24 * 60 * 60 * 1e3)));
}
function getSeasonWeeks(region, date = /* @__PURE__ */ new Date()) {
  const { start, end } = getSeasonDates(region, date);
  const total = end.getTime() - start.getTime();
  return Math.round(total / (7 * 24 * 60 * 60 * 1e3));
}
function getDaysUntilSeasonStart(region, date = /* @__PURE__ */ new Date()) {
  if (region === "year_round") return 0;
  if (isInSeason(region, date)) return 0;
  const preset = SEASON_PRESETS[region];
  const year = date.getFullYear();
  let nextStart = toDate(preset.start, year);
  if (nextStart.getTime() <= date.getTime()) {
    nextStart = toDate(preset.start, year + 1);
  }
  const msUntil = nextStart.getTime() - date.getTime();
  return Math.ceil(msUntil / (24 * 60 * 60 * 1e3));
}

// src/schemas/invitation.ts
import { z } from "zod";
var invitationStatusEnum = z.enum(["pending", "accepted", "expired", "revoked"]);
var orgRoleEnum = z.enum(["owner", "admin", "member"]);
var inviteRoleEnum = z.enum(["admin", "member"]);
var createInvitationSchema = z.object({
  email: z.string().email("Invalid email address"),
  role: inviteRoleEnum.default("member")
});
var invitationSchema = z.object({
  // `id` and `organizationId` / `invitedById` are integer FKs in the DB and
  // returned as-is by the API. `pid` (uuid) is the public identifier used in
  // URLs and the accept-token flow.
  id: z.number().int(),
  pid: z.string().uuid().optional(),
  organizationId: z.number().int(),
  email: z.string().email(),
  role: orgRoleEnum,
  status: invitationStatusEnum,
  token: z.string(),
  expiresAt: z.coerce.date(),
  invitedById: z.number().int(),
  createdAt: z.coerce.date(),
  acceptedAt: z.coerce.date().nullable()
});
var invitationTokenInfoSchema = z.object({
  // The accept-page consumer cares about the public UUID, not the DB FK.
  pid: z.string().uuid(),
  email: z.string().email(),
  role: orgRoleEnum,
  status: invitationStatusEnum,
  expiresAt: z.coerce.date(),
  organizationId: z.number().int(),
  organizationName: z.string(),
  organizationPid: z.string().uuid(),
  inviterName: z.string(),
  emailExists: z.boolean(),
  boats: z.array(
    z.object({
      pid: z.string().uuid(),
      name: z.string()
    })
  )
});
var orgMemberSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  role: orgRoleEnum,
  joinedAt: z.coerce.date()
});

// src/schemas/auth.ts
var loginSchema = z2.object({
  email: z2.string().email("Invalid email address"),
  password: z2.string().min(1, "Password is required")
});
var registerSchema = z2.object({
  email: z2.string().email("Invalid email address"),
  password: z2.string().min(8, "Password must be at least 8 characters"),
  name: z2.string().min(1, "Name is required").max(100, "Name is too long"),
  invitationToken: z2.string().min(1, "Invitation token is required")
});
var refreshTokenSchema = z2.object({
  refreshToken: z2.string().min(1, "Refresh token is required")
});
var forgotPasswordSchema = z2.object({
  email: z2.string().email("Invalid email address")
});
var resetPasswordSchema = z2.object({
  token: z2.string().min(1, "Reset token is required"),
  password: z2.string().min(8, "Password must be at least 8 characters")
});
var authUserOrgSchema = z2.object({
  id: z2.string().uuid(),
  name: z2.string(),
  slug: z2.string(),
  seasonRegion: z2.enum(SEASON_REGIONS),
  setupCompletedAt: z2.string().nullable(),
  role: orgRoleEnum
});
var authUserSchema = z2.object({
  id: z2.string().uuid(),
  email: z2.string().email(),
  name: z2.string(),
  isSuperAdmin: z2.boolean(),
  organizations: z2.array(authUserOrgSchema)
});
var authResponseSchema = z2.object({
  accessToken: z2.string(),
  refreshToken: z2.string(),
  user: authUserSchema
});
var teamSizeOptions = ["just-me", "2-5", "6-20", "21-50", "51+"];
var getStartedSchema = z2.object({
  email: z2.string().email("Invalid email address")
});
var completeOnboardingSchema = z2.object({
  token: z2.string().min(1, "Token is required"),
  name: z2.string().min(1, "Name is required").max(100, "Name is too long"),
  organizationName: z2.string().min(1, "Organization name is required").max(255, "Organization name is too long"),
  teamSize: z2.enum(teamSizeOptions),
  password: z2.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z2.string().min(1, "Please confirm your password")
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var completeOnboardingNewOrgSchema = z2.object({
  token: z2.string().min(1, "Token is required"),
  organizationName: z2.string().min(1, "Organization name is required").max(255, "Organization name is too long"),
  teamSize: z2.enum(teamSizeOptions)
});

// src/schemas/beta-signup.ts
import { z as z3 } from "zod";
var fleetSizeOptions = ["1-5", "6-20", "21-50", "50+"];
var betaSignupSchema = z3.object({
  email: z3.string().email("Invalid email address").max(255, "Email is too long"),
  company: z3.string().min(2, "Company is required").max(255, "Company name is too long"),
  fleetSize: z3.enum(fleetSizeOptions, {
    error: "Pick a fleet size"
  }),
  source: z3.string().max(255).optional()
});

// src/schemas/boat.ts
import { z as z4 } from "zod";
var boatSchema = z4.object({
  id: z4.string().uuid(),
  name: z4.string(),
  kind: z4.string(),
  model: z4.string().nullable(),
  shipyard: z4.string().nullable(),
  year: z4.number().nullable(),
  lengthM: z4.string().nullable(),
  beamM: z4.string().nullable(),
  draughtM: z4.string().nullable(),
  cabins: z4.number().nullable(),
  berths: z4.number().nullable(),
  wc: z4.number().nullable(),
  maxPeople: z4.number().nullable(),
  charterName: z4.string().nullable(),
  homeBase: z4.string().nullable(),
  defaultCurrency: z4.string().nullable(),
  commissionPct: z4.string().nullable(),
  isActive: z4.boolean(),
  source: z4.string(),
  externalId: z4.string().nullable(),
  createdAt: z4.coerce.date(),
  updatedAt: z4.coerce.date()
});
var paginatedBoatsSchema = z4.object({
  data: z4.array(boatSchema),
  meta: z4.object({
    total: z4.number(),
    page: z4.number(),
    limit: z4.number(),
    totalPages: z4.number()
  })
});
var pricingPeriodResponseSchema = z4.object({
  id: z4.string().uuid(),
  dateFrom: z4.string(),
  dateTo: z4.string(),
  product: z4.string(),
  price: z4.string(),
  currency: z4.string(),
  listPrice: z4.string().nullable(),
  discountPct: z4.string().nullable(),
  source: z4.string(),
  externalId: z4.string().nullable(),
  createdAt: z4.coerce.date(),
  updatedAt: z4.coerce.date()
});
var priceSnapshotResponseSchema = z4.object({
  id: z4.string().uuid(),
  dateFrom: z4.string(),
  dateTo: z4.string(),
  product: z4.string(),
  price: z4.string(),
  currency: z4.string(),
  listPrice: z4.string().nullable(),
  discountPct: z4.string().nullable(),
  fetchedAt: z4.coerce.date(),
  source: z4.string()
});

// src/schemas/dashboard.ts
import { z as z5 } from "zod";
var widgetTypeSchema = z5.enum([
  "occupancy-by-month-chart",
  "occupancy-by-week-chart",
  "revenue-by-boat-chart",
  "revenue-vs-target",
  "activity-feed",
  "market-this-week",
  "season-progress",
  "getting-started"
]);
var widgetSizeSchema = z5.enum(["third", "half", "full"]);
var dashboardWidgetSchema = z5.object({
  id: z5.string(),
  type: widgetTypeSchema,
  position: z5.number().int().min(0),
  config: z5.record(z5.string(), z5.unknown()).default({})
});
var dashboardLayoutSchema = z5.object({
  widgets: z5.array(dashboardWidgetSchema)
});
var updateDashboardLayoutSchema = dashboardLayoutSchema;

// src/schemas/income.ts
import { z as z6 } from "zod";
var incomeOwnershipSchema = z6.enum(["external", "company"]);
var seasonYear = z6.number().int().min(2020, "Year must be 2020 or later").max((/* @__PURE__ */ new Date()).getFullYear() + 5, "Year is too far in the future");
var upsertBoatIncomeSettingsSchema = z6.object({
  year: seasonYear,
  ownership: incomeOwnershipSchema,
  /** The external owner's share of the net, 0–100; null until agreed (not 0%). */
  sharePct: z6.number().min(0, "Share must be zero or positive").max(100, "Share cannot exceed 100").nullable()
}).refine((v) => v.ownership !== "company" || v.sharePct === null, {
  message: "A company-owned boat has no owner share",
  path: ["sharePct"]
});
var setSeasonTaxRateSchema = z6.object({
  year: seasonYear,
  taxRatePct: z6.number().min(0, "Rate must be zero or positive").max(100, "Rate cannot exceed 100")
});

// src/schemas/integration.ts
import { z as z7 } from "zod";
var integrationProviderEnum = z7.enum(["mmk", "nausys"]);
var integrationStatusEnum = z7.enum(["active", "inactive", "error", "pending_validation"]);
var createIntegrationSchema = z7.object({
  provider: integrationProviderEnum,
  name: z7.string().min(1, "Name is required").max(255),
  credentials: z7.record(z7.string(), z7.string())
});
var updateIntegrationSchema = z7.object({
  name: z7.string().min(1).max(255).optional(),
  credentials: z7.record(z7.string(), z7.string()).optional(),
  status: z7.enum(["active", "inactive"]).optional()
});

// src/schemas/occupancy.ts
import { z as z8 } from "zod";
var occupancyStatusSchema = z8.enum([
  "available",
  "booked",
  "option",
  "maintenance",
  "transit",
  "unavailable"
]);

// src/schemas/organization.ts
import { z as z9 } from "zod";
var organizationSchema = z9.object({
  id: z9.string().uuid(),
  name: z9.string(),
  slug: z9.string(),
  seasonRegion: z9.enum(SEASON_REGIONS).default("mediterranean"),
  settings: z9.record(z9.string(), z9.unknown()).nullable(),
  createdAt: z9.coerce.date(),
  updatedAt: z9.coerce.date()
});
var createOrganizationSchema = z9.object({
  name: z9.string().min(1, "Name is required").max(255),
  slug: z9.string().min(1).max(100).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be URL-safe").optional()
});
var updateOrganizationSchema = z9.object({
  name: z9.string().min(1).max(255).optional(),
  slug: z9.string().min(1).max(100).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Slug must be URL-safe").optional(),
  seasonRegion: z9.enum(SEASON_REGIONS).optional(),
  settings: z9.record(z9.string(), z9.unknown()).optional()
});
var organizationQuerySchema = z9.object({
  limit: z9.coerce.number().int().positive().max(100).default(10),
  offset: z9.coerce.number().int().min(0).default(0)
});
var paginatedOrganizationsSchema = z9.object({
  data: z9.array(organizationSchema),
  total: z9.number(),
  limit: z9.number(),
  offset: z9.number()
});

// src/schemas/owner.ts
import { z as z10 } from "zod";
var upsertOwnerSchema = z10.object({
  name: z10.string().min(1, "Name is required").max(100),
  // Optional (#216): an owner can get the status link copied instead of emailed.
  email: z10.string().email("Valid email required").nullable().optional(),
  boatPids: z10.array(z10.string().uuid("Boat ids must be UUIDs")).default([])
});

// src/schemas/revenue-goal.ts
import { z as z11 } from "zod";
var upsertRevenueGoalSchema = z11.object({
  year: z11.number().int().min(2020, "Year must be 2020 or later").max((/* @__PURE__ */ new Date()).getFullYear() + 5, "Year is too far in the future"),
  targetRevenue: z11.number().nonnegative("Target revenue must be zero or positive").max(1e8, "Target revenue is unreasonably large"),
  targetWeeks: z11.number().int().min(0, "Target weeks must be zero or positive").max(52, "Target weeks cannot exceed 52"),
  currency: z11.string().length(3, "Currency must be a 3-letter ISO 4217 code").regex(/^[A-Z]{3}$/, "Currency must be uppercase"),
  notes: z11.string().max(500, "Notes cannot exceed 500 characters").optional()
});

// src/schemas/user.ts
import { z as z12 } from "zod";
var userSchema = z12.object({
  id: z12.string().uuid(),
  email: z12.string().email(),
  name: z12.string(),
  isActive: z12.boolean(),
  createdAt: z12.coerce.date(),
  updatedAt: z12.coerce.date()
});
var updateUserSchema = z12.object({
  name: z12.string().min(1).max(100).optional(),
  isActive: z12.boolean().optional()
});
var userQuerySchema = z12.object({
  page: z12.coerce.number().int().positive().default(1),
  limit: z12.coerce.number().int().positive().max(100).default(10),
  search: z12.string().optional(),
  isActive: z12.coerce.boolean().optional()
});
var paginatedUsersSchema = z12.object({
  data: z12.array(userSchema),
  meta: z12.object({
    total: z12.number(),
    page: z12.number(),
    limit: z12.number(),
    totalPages: z12.number()
  })
});

// src/schemas/watchlist.ts
import { z as z13 } from "zod";
var notificationEventSchema = z13.object({
  id: z13.string().uuid(),
  boatId: z13.string().uuid(),
  type: z13.string(),
  payload: z13.record(z13.string(), z13.unknown()).nullable(),
  readAt: z13.coerce.date().nullable(),
  createdAt: z13.coerce.date()
});
var paginatedNotificationEventsSchema = z13.object({
  data: z13.array(notificationEventSchema),
  meta: z13.object({
    total: z13.number(),
    page: z13.number(),
    limit: z13.number(),
    totalPages: z13.number()
  })
});

// src/constants/queues.ts
var QueueNames = {
  EMAIL: "email",
  NOTIFICATION: "notification",
  EXPORT: "export",
  SYNC: "sync",
  MARKET_SYNC: "market-sync"
};
var JobNames = {
  // Email jobs
  SEND_WELCOME_EMAIL: "send-welcome-email",
  SEND_PASSWORD_RESET_EMAIL: "send-password-reset-email",
  SEND_VERIFICATION_EMAIL: "send-verification-email",
  SEND_INVITATION_EMAIL: "send-invitation-email",
  SEND_ONBOARDING_EMAIL: "send-onboarding-email",
  SEND_EXISTING_USER_ONBOARDING_EMAIL: "send-existing-user-onboarding-email",
  // Notification jobs
  SEND_PUSH_NOTIFICATION: "send-push-notification",
  SEND_IN_APP_NOTIFICATION: "send-in-app-notification",
  // Export jobs
  EXPORT_CSV: "export-csv",
  EXPORT_PDF: "export-pdf",
  // Sync jobs
  SYNC_INTEGRATION: "sync-integration",
  // Market sync jobs
  FETCH_MARKET_BOATS_DUMP: "fetch-market-boats-dump",
  IMPORT_MARKET_BOATS_DUMP: "import-market-boats-dump",
  SYNC_EQUIPMENT: "sync-equipment",
  SYNC_MARKET_PRICING: "sync-market-pricing",
  SYNC_MARKET_PRICING_PERIODIC: "sync-market-pricing-periodic",
  SYNC_MARKET_PRICING_DAILY_FULL: "sync-market-pricing-daily-full",
  SYNC_MARKET_PRICING_CURRENT_YEAR: "sync-market-pricing-current-year",
  EVALUATE_PRICE_ALERTS: "evaluate-price-alerts",
  REFRESH_COMP_SET_BOATS: "refresh-comp-set-boats",
  POPULATE_COMP_SET: "populate-comp-set",
  GENERATE_PRICING_SUGGESTIONS: "generate-pricing-suggestions",
  DETECT_DEALS: "detect-deals",
  DETECT_ORPHAN_GAPS: "detect-orphan-gaps",
  SYNC_LOCATIONS: "sync-locations",
  SYNC_COMPANIES: "sync-companies",
  SYNC_CHARTER_FLEET: "sync-charter-fleet",
  // Lightweight per-org reservations-only sync (issue: charter-sync branch).
  // Runs on a tighter cadence than the full charter-fleet sync so booking
  // state stays fresh without re-pulling yachts/pricing/availability.
  SYNC_CHARTER_RESERVATIONS: "sync-charter-reservations",
  SYNC_MARKET_AVAILABILITY_ACTIVE: "sync-market-availability-active",
  SYNC_MARKET_AVAILABILITY_PREVIOUS: "sync-market-availability-previous",
  // Alert email jobs
  SEND_PRICE_ALERT_EMAIL: "send-price-alert-email",
  // Per-run consolidated digest: one email per (org, user) per evaluation run.
  SEND_ALERT_DIGEST_EMAIL: "send-alert-digest-email",
  SEND_OWNER_STATUS_LINK_EMAIL: "send-owner-status-link-email",
  // Firm sync jobs
  SYNC_FIRM_FLEET: "sync-firm-fleet"
};

// src/constants/admin-sync-jobs.ts
var AdminSyncJobIds = {
  PRICING: "pricing",
  WEEKLY_PRICING_PERIODIC: "weekly-pricing-periodic",
  WEEKLY_PRICING_FULL: "weekly-pricing-full",
  WEEKLY_PRICING_CURRENT_YEAR: "weekly-pricing-current-year",
  BOATS_FETCH: "boats-fetch",
  BOATS_IMPORT: "boats-import",
  EQUIPMENT: "equipment",
  LOCATIONS: "locations",
  COMPANIES: "companies",
  AVAILABILITY_ACTIVE: "availability-active",
  AVAILABILITY_PREVIOUS: "availability-previous"
};
var ADMIN_SYNC_JOBS = [
  {
    id: "pricing",
    name: "Market Pricing (Tracked)",
    description: "Fetch pricing for tracked boats only (legacy)",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "weekly-pricing-periodic",
    name: "Weekly Pricing (Periodic)",
    description: "Sync weekly Saturday-to-Saturday pricing for all boats (future weeks)",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "weekly-pricing-full",
    name: "Weekly Pricing (Full)",
    description: "Sync weekly Saturday-to-Saturday pricing for all boats (all 52 weeks)",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "weekly-pricing-current-year",
    name: "Weekly Pricing (Current Year)",
    description: "Sync weekly Saturday-to-Saturday pricing for all boats (all weeks in current year)",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "boats-fetch",
    name: "Boats Fetch + Import",
    description: "Download /yachts dump and import into market_boats (auto-chains import)",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "boats-import",
    name: "Boats Re-import",
    description: "Re-import the latest downloaded dump (debug)",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "equipment",
    name: "Equipment",
    description: "Sync MMK equipment master list",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "locations",
    name: "Locations",
    description: "Sync world regions, countries, sailing areas, bases",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "companies",
    name: "Companies",
    description: "Sync MMK charter companies",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "availability-active",
    name: "Availability (Current + Next Year)",
    description: "Sync MMK boat availability for current and next year \u2014 the fast daily path",
    queue: QueueNames.MARKET_SYNC
  },
  {
    id: "availability-previous",
    name: "Availability (Previous Year)",
    description: "Sync MMK boat availability for previous year \u2014 run manually to pick up retroactive corrections",
    queue: QueueNames.MARKET_SYNC
  }
];

// src/constants/charter-emails.ts
var CHARTER_EMAIL_KINDS = ["invite", "follow_up", "newsletter"];
var CHARTER_EMAIL_LABELS = {
  invite: "Invite",
  follow_up: "Follow-up",
  newsletter: "Newsletter"
};

// src/constants/charter-week.ts
var DAY_MS = 864e5;
var CHARTER_WEEK_DAYS = 7;
function charterWeekStart(isoDate) {
  const ms = Date.parse(`${isoDate}T00:00:00Z`);
  const daysSinceSaturday = (new Date(ms).getUTCDay() + 1) % 7;
  return new Date(ms - daysSinceSaturday * DAY_MS).toISOString().slice(0, 10);
}
function charterWeekEnd(weekStart) {
  const ms = Date.parse(`${weekStart}T00:00:00Z`);
  return new Date(ms + (CHARTER_WEEK_DAYS - 1) * DAY_MS).toISOString().slice(0, 10);
}
function bookedWeeksFromDays(days) {
  return days / CHARTER_WEEK_DAYS;
}
function roundBookedWeeks(weeks) {
  return Math.round(weeks * 100) / 100;
}

// src/constants/events.ts
var SocketEvents = {
  // Connection events
  CONNECTION: "connection",
  DISCONNECT: "disconnect",
  ERROR: "error",
  // Authentication events
  AUTH_SUCCESS: "auth:success",
  AUTH_ERROR: "auth:error",
  // User events
  USER_UPDATED: "user:updated",
  USER_DELETED: "user:deleted",
  USER_STATUS_CHANGED: "user:status-changed",
  // Notification events
  NOTIFICATION_NEW: "notification:new",
  NOTIFICATION_READ: "notification:read",
  // Sync events
  SYNC_COMPLETED: "sync:completed",
  SYNC_FAILED: "sync:failed",
  // Pricing events
  PRICE_ALERT_TRIGGERED: "price-alert:triggered",
  // Comp-set events
  COMP_SET_SYNC_DONE: "comp-set:sync-done",
  COMP_SET_SYNC_FAILED: "comp-set:sync-failed",
  // Firm sync events (onboarding)
  FIRM_SYNC_PHASE_STARTED: "firm-sync:phase-started",
  FIRM_SYNC_PHASE_PROGRESS: "firm-sync:phase-progress",
  FIRM_SYNC_PHASE_COMPLETED: "firm-sync:phase-completed",
  FIRM_SYNC_COMPLETED: "firm-sync:completed",
  FIRM_SYNC_FAILED: "firm-sync:failed"
};

// src/constants/integrations.ts
var IntegrationProviders = {
  mmk: {
    label: "Booking Manager (MMK)",
    fields: [{ key: "apiKey", label: "API Key", type: "text" }]
  },
  nausys: {
    label: "NauSYS",
    fields: [
      { key: "username", label: "Username", type: "text" },
      { key: "password", label: "Password", type: "password" },
      { key: "operatorId", label: "Operator ID", type: "text" }
    ]
  }
};

// src/constants/mmk-reservation-statuses.ts
var MMK_RESERVATION_STATUS = {
  reservation: 1,
  option: 2,
  optionExpired: 3,
  service: 4,
  cancelled: 5,
  ownerWeek: 6,
  offer: 7,
  custom: 8,
  optionOnWaiting: 9,
  regatta: 10,
  sleepAboard: 11
};
var MMK_RESERVATION_STATUS_NAMES = {
  [MMK_RESERVATION_STATUS.reservation]: "Reservation",
  [MMK_RESERVATION_STATUS.option]: "Option",
  [MMK_RESERVATION_STATUS.optionExpired]: "Option Expired",
  [MMK_RESERVATION_STATUS.service]: "Service",
  [MMK_RESERVATION_STATUS.cancelled]: "Cancelled",
  [MMK_RESERVATION_STATUS.ownerWeek]: "Owner Week",
  [MMK_RESERVATION_STATUS.offer]: "Offer",
  [MMK_RESERVATION_STATUS.custom]: "Custom",
  [MMK_RESERVATION_STATUS.optionOnWaiting]: "Option on Waiting",
  [MMK_RESERVATION_STATUS.regatta]: "Regatta",
  [MMK_RESERVATION_STATUS.sleepAboard]: "Sleep Aboard"
};

// src/index.ts
import { z as z14 } from "zod";
export {
  ADMIN_SYNC_JOBS,
  AdminSyncJobIds,
  CHARTER_EMAIL_KINDS,
  CHARTER_EMAIL_LABELS,
  CHARTER_WEEK_DAYS,
  IntegrationProviders,
  JobNames,
  MMK_RESERVATION_STATUS,
  MMK_RESERVATION_STATUS_NAMES,
  QueueNames,
  SEASON_PRESETS,
  SEASON_REGIONS,
  SocketEvents,
  authResponseSchema,
  authUserOrgSchema,
  authUserSchema,
  betaSignupSchema,
  boatSchema,
  bookedWeeksFromDays,
  charterWeekEnd,
  charterWeekStart,
  completeOnboardingNewOrgSchema,
  completeOnboardingSchema,
  createIntegrationSchema,
  createInvitationSchema,
  createOrganizationSchema,
  dashboardLayoutSchema,
  dashboardWidgetSchema,
  fleetSizeOptions,
  forgotPasswordSchema,
  getDaysUntilSeasonStart,
  getSeasonProgress,
  getSeasonWeeks,
  getStartedSchema,
  getWeeksRemaining,
  incomeOwnershipSchema,
  integrationProviderEnum,
  integrationStatusEnum,
  invitationSchema,
  invitationStatusEnum,
  invitationTokenInfoSchema,
  inviteRoleEnum,
  isInSeason,
  loginSchema,
  notificationEventSchema,
  occupancyStatusSchema,
  orgMemberSchema,
  orgRoleEnum,
  organizationQuerySchema,
  organizationSchema,
  paginatedBoatsSchema,
  paginatedNotificationEventsSchema,
  paginatedOrganizationsSchema,
  paginatedUsersSchema,
  priceSnapshotResponseSchema,
  pricingPeriodResponseSchema,
  refreshTokenSchema,
  registerSchema,
  resetPasswordSchema,
  roundBookedWeeks,
  setSeasonTaxRateSchema,
  teamSizeOptions,
  updateDashboardLayoutSchema,
  updateIntegrationSchema,
  updateOrganizationSchema,
  updateUserSchema,
  upsertBoatIncomeSettingsSchema,
  upsertOwnerSchema,
  upsertRevenueGoalSchema,
  userQuerySchema,
  userSchema,
  widgetSizeSchema,
  widgetTypeSchema,
  z14 as z
};
//# sourceMappingURL=index.js.map